import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { createMemo, updateMemo, errorMessage } from './memoApi.js'

const MAX_LENGTH = 10000;

// メモの新規作成・編集ダイアログ
// 新規: personalCode / workplaceInstitutionCode で対象を指定する
//       医師メモの場合は hospitalOptions（所属病院）から病院を併記できる
// 編集: memo に一覧の行データを渡す（対象は変更しない）
export default function MemoEditDialog(props) {
  const {
    open, onClose, token, memo,
    personalCode, workplaceInstitutionCode, targetLabel, hospitalOptions,
  } = props;

  const isEdit = Boolean(memo);

  const [content, setContent] = useState('');
  const [hospitalCode, setHospitalCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }
    setContent(isEdit ? memo.content : '');
    setHospitalCode(isEdit ? memo.workplace_institution_code : (workplaceInstitutionCode || ''));
    setError('');
  }, [open, memo, isEdit, workplaceInstitutionCode]);

  const label = isEdit
    ? [memo.doctor_name, memo.formal_name].filter((name) => name).join(' / ')
    : targetLabel;

  const showHospitalSelect = !isEdit && personalCode && hospitalOptions && hospitalOptions.length > 0;

  const handleSave = () => {
    setSaving(true);
    setError('');

    const request = isEdit
      ? updateMemo(token, memo.id, { content: content })
      : createMemo(token, {
        personal_code: personalCode || '',
        workplace_institution_code: hospitalCode,
        content: content,
      });

    request
      .then(() => {
        setSaving(false);
        onClose(true);
      })
      .catch((err) => {
        setSaving(false);
        setError(errorMessage(err));
      });
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? 'メモを編集' : 'メモを追加'}</DialogTitle>
      <DialogContent>
        {error &&
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        }
        <Typography sx={{ mb: 2 }}>対象：{label}</Typography>

        {showHospitalSelect &&
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="memo-hospital-label">病院（任意）</InputLabel>
            <Select
              labelId="memo-hospital-label"
              label="病院（任意）"
              value={hospitalCode}
              onChange={(event) => setHospitalCode(event.target.value)}
            >
              <MenuItem value="">指定しない</MenuItem>
              {hospitalOptions.map((hospital) => (
                <MenuItem key={hospital.code} value={hospital.code}>{hospital.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        }

        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={6}
          maxRows={20}
          label="メモ"
          value={content}
          onChange={(event) => setContent(event.target.value)}
          inputProps={{ maxLength: MAX_LENGTH }}
          helperText={`${content.length} / ${MAX_LENGTH}`}
        />
        {isEdit &&
          <Box sx={{ mt: 1, color: 'text.secondary', fontSize: 14 }}>
            作成者：{memo.author_name}
          </Box>
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>キャンセル</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || content.trim() === ''}
        >
          保存
        </Button>
      </DialogActions>
    </Dialog>
  );
}

MemoEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  memo: PropTypes.object,
  personalCode: PropTypes.string,
  workplaceInstitutionCode: PropTypes.string,
  targetLabel: PropTypes.string,
  hospitalOptions: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
  })),
};
